'use client';

// #region IMPORT
import { useState, useEffect } from 'react';
import Autoplay from 'embla-carousel-autoplay';

// @mui
import { Box, Stack, Container, Typography, Unstable_Grid2 as Grid } from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';

// components
import { Carousel, useCarousel, CarouselArrowFloatButtons } from 'src/components/carousel';

import { TestimonialItemContent, TestimonialItemThumbnail } from './testimonial-item';
// #endregion
// ----------------------------------------------------------------------

// #region INTERFACE
export type ITestimonialProps = {
  id: string;
  name: string;
  review: string;
  role?: string;
  avatar?: string;
  rating?: number;
  postDate?: Date | string | number;
};
// #endregion
const testimonials = [
  {
    id: '0',
    name: 'Quỳnh Anh',
    review:
      'CenterUp đã thực sự làm thay đổi cách quản lý trung tâm đào tạo của chúng tôi. Với những tính năng toàn diện, chúng tôi có thể dễ dàng quản lý việc đăng ký học viên, lịch học và tài liệu đào tạo. Phần mềm này đã tối ưu hóa quy trình làm việc của chúng tôi và cải thiện hiệu suất làm việc tổng thể.',
    role: 'Founder & CEO - Amslink',
    avatar: '/assets/landing/testimonial/amslink.jpg',
    rating: 5,
    postDate: '07/04/2023',
  },
  {
    id: '1',
    name: 'Võ Mai Thương',
    review:
      'Kể từ khi triển khai CenterUp, chúng tôi đã chứng kiến một sự thay đổi đáng kể trong hoạt động của MoyArt. Các tính năng mạnh mẽ của phần mềm này cho phép chúng tôi dễ dàng theo dõi tiến trình học tập của học viên, quản lý phân công giảng viên và tạo ra báo cáo chi tiết.',
    role: 'Founder & CEO - MoyArt',
    avatar: '/assets/landing/testimonial/MoyArt.jpg',
    rating: 5,
    postDate: '12/05/2023',
  },
  {
    id: '2',
    name: 'Tùng Đàm',
    review:
      'Chúng tôi rất hài lòng với phần mềm CenterUp. Giao diện thân thiện và các tính năng mạnh mẽ của nó đã đơn giản hóa các công việc quản lý của chúng tôi. ',
    role: 'Founder & CEO - AtSchool',
    avatar: '/assets/landing/testimonial/AtSchool.jpg',
    rating: 5,
    postDate: '07/04/2023',
  },
  {
    id: '3',
    name: 'Ngân JP',
    review:
      'Chúng tôi đã tiết kiệm rất nhiều thời gian và công sức kể từ khi sử dụng CenterUp.  Nhiều tính năng linh hoạt và dễ sử dụng giúp chúng tôi dễ dàng theo dõi học viên, quản lý lớp học và tạo lịch học một cách hiệu quả.',
    role: 'Founder & CEO - BrainSTEM',
    avatar: '/assets/landing/testimonial/BrainSTEM.jpg',
    rating: 5,
    postDate: '07/04/2023',
  },
  {
    id: '4',
    name: 'Linh Đặng',
    review:
      'CenterUp đã giúp chúng tôi tối ưu xếp lịch dạy và quản lý công việc một cách hiệu quả. Bảng công tự động và tính năng đánh giá kết quả giảng dạy toàn diện đã giúp chúng tôi tiết kiệm thời gian và nâng cao hiệu suất làm việc của giáo viên',
    role: 'Founder & CEO - BZ English',
    avatar: '/assets/landing/testimonial/BZEnglish.jpg',
    rating: 5,
    postDate: '07/04/2023',
  },
] as ITestimonialProps[];

export const TestimonialElearning = () => {
  const smUp = useResponsive('up', 'sm');
  const [selected, setSelected] = useState(0);

  const carouselContent = useCarousel({ loop: true, align: 'center', slidesToShow: 1 }, [
    Autoplay({ playOnInit: true, delay: 3000 }),
  ]);

  const carouselThumbnail = useCarousel({
    align: 'center',
    slideSpacing: '10px',
    loop: true,
    slidesToShow: smUp ? 5 : 3,
  });

  useEffect(() => {
    const { mainApi } = carouselContent;
    const thumbApi = carouselThumbnail.mainApi;

    if (!mainApi || !thumbApi) return;

    const handleSelect = () => {
      const index = mainApi.selectedScrollSnap();
      setSelected(index);
      thumbApi.scrollTo(index);
    };

    mainApi.on('select', handleSelect);
    // eslint-disable-next-line consistent-return
    return () => {
      mainApi.off('select', handleSelect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselContent.mainApi, carouselThumbnail.mainApi]);

  return (
    <Box
      position="relative"
      py={{ xs: 8, sm: 6, md: 10 }}
      sx={{
        bgcolor: 'background.neutral',
        textAlign: 'center',
        overflow: 'hidden',
      }}
    >
      <Container sx={{ position: 'relative' }}>
        {smUp && (
          <Typography variant="h2" fontWeight="bold" sx={{ mb: 5 }}>
            Khách hàng nói gì về chúng tôi
          </Typography>
        )}
        <Grid container spacing={3} justifyContent="center">
          <Grid xs={11} md={6}>
            <CarouselArrowFloatButtons
              {...carouselContent.arrows}
              options={carouselContent.options}
              slotProps={{
                prevBtn: {
                  sx: { left: 0, bgcolor: 'transparent', color: 'default' },
                },
                nextBtn: {
                  sx: { right: 0, bgcolor: 'transparent', color: 'default' },
                },
              }}
              sx={{ color: 'primary.contrastText' }}
            />
            <Carousel carousel={carouselContent}>
              {testimonials.map((testimonial) => (
                <TestimonialItemContent key={testimonial.id} testimonial={testimonial} />
              ))}
            </Carousel>
            <Box sx={{ mb: 3, mx: 'auto', maxWidth: { xs: 360, sm: 420 }, overflow: 'hidden' }}>
              <Carousel
                carousel={carouselThumbnail}
                slotProps={{
                  slide: {
                    paddingLeft: smUp ? 0 : 1.5,
                    alignItems: 'center',
                    display: 'flex',
                  },
                }}
              >
                {testimonials.map((testimonial, index) => (
                  <TestimonialItemThumbnail
                    key={index}
                    testimonial={testimonial}
                    isSelected={selected === index}
                  />
                ))}
              </Carousel>
            </Box>
            {testimonials.map(
              (testimonial, index) =>
                selected === index && (
                  <Stack key={testimonial.id} spacing={0.5}>
                    <Typography variant="h6" component="p">
                      {testimonial.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {testimonial.role}
                    </Typography>
                  </Stack>
                )
            )}
          </Grid>
        </Grid>
      </Container>
      <Box
        position="absolute"
        bottom={0}
        left={0}
        width="100%"
        height="10px"
        zIndex={2}
        sx={{
          background: 'linear-gradient(to top, rgba(0,0,0,0.02), rgba(0,0,0,0))',
        }}
      />
    </Box>
  );
};
